import type {
  VbenFormSchema as FormSchema,
  VbenFormProps,
} from '@vben/common-ui'

import type { ComponentType } from './component'

import {
  setupVbenForm as _setupVbenForm,
  useVbenForm as useForm,
  z,
} from '@vben/common-ui'

import { watchDebounced } from '@vueuse/core'
import { get, merge, pick } from 'lodash-es'

import { $t } from '#/locales'

export function setupVbenForm() {
  _setupVbenForm<ComponentType>({
    config: {
      // ant design vue组件库默认都是 v-model:value
      baseModelPropName: 'value',
      // 一些组件是 v-model:checked 或者 v-model:fileList
      modelPropNameMap: {
        Checkbox: 'checked',
        Radio: 'checked',
        Switch: 'checked',
        Upload: 'fileList',
      },
    },
    defineRules: {
      // 输入项目必填国际化适配
      required: (value, _params, ctx) => {
        if (value === undefined || value === null || value.length === 0) {
          return $t('ui.formRules.required', [ctx.label])
        }
        return true
      },
      // 选择项目必填国际化适配
      selectRequired: (value, _params, ctx) => {
        if (value === undefined || value === null) {
          return $t('ui.formRules.selectRequired', [ctx.label])
        }
        return true
      },
      // 上传项目必填国际化适配
      uploadRequired: (value, _params, ctx) => {
        if (value === undefined || value === null || value.length === 0) {
          return $t('ui.formRules.uploadRequired', [ctx.label])
        }
        return true
      },
    },
  })
}

const _useVbenForm = useForm<ComponentType>

const DEFAULT_FORM_PROPS: VbenFormProps<ComponentType> = {
  commonConfig: {
    componentProps: {
      class: 'w-full',
    },
    labelWidth: 72,
    labelClass: 'justify-start',
  },
  showDefaultActions: false,
  compact: false,
}

const useVbenForm = (
  options: MaybeRefOrGetter<VbenFormProps<ComponentType>>,
  depKeys: string[] = [],
): ReturnType<typeof _useVbenForm> => {
  const computedOptions = computed(() => toValue(options))

  const watchOptions = {
    deep: true,
    debounce: 70,
  }

  if (depKeys.length > 0) {
    watchDebounced(
      depKeys.map((key) => () => get(computedOptions.value, key)),
      () => {
        formApi.setState(pick(computedOptions.value, depKeys))
      },
      watchOptions,
    )
  } else {
    watchDebounced(
      computedOptions,
      (val) => {
        formApi.setState(val)
      },
      watchOptions,
    )
  }

  const [Form, formApi] = _useVbenForm(
    merge({}, DEFAULT_FORM_PROPS, computedOptions.value),
  )
  return [Form, formApi]
}

export { useVbenForm, z }

export type VbenFormSchema = FormSchema<ComponentType>
export type { VbenFormProps }
